import { type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGlobalState } from '@/stores/globalState'
import { PH_UNMASK } from '@/lib/privacyMask'

const MODULE_LABELS: Record<string, string> = {
  BOOKS: 'Books',
  MY_GUIDES: 'Home',
  EVALUATION: 'Profile',
  LEARNING_JOURNEY: 'Learning Journey',
}

interface MiddlePanelProps {
  children?: ReactNode
}

// Modules whose surfaces own the full content panel (edge-to-edge paper:
// the redesigned Books shelf and the placement flow). Everything else
// keeps the legacy p-6 inset until its redesign lands.
const FULL_BLEED_MODULES = new Set(['BOOKS', 'EVALUATION'])

export function MiddlePanel({ children }: MiddlePanelProps) {
  const activeModule = useGlobalState((s) => s.activeModule)
  const workspaceView = useGlobalState((s) => s.workspaceView)
  // The MY_GUIDES gallery landing is full-bleed: both surfaces it can render
  // (the redesigned Home and the placement gate for an un-evaluated learner)
  // own their own edge-to-edge padding. Other MY_GUIDES views (canvas,
  // focused) keep the legacy p-6 inset.
  const homeFullBleed = activeModule === 'MY_GUIDES' && workspaceView === 'GALLERY'
  // The generating-games wait screen owns its own padding too.
  const generatingFullBleed = activeModule === 'MY_GUIDES' && workspaceView === 'GENERATING'
  const fullBleed = FULL_BLEED_MODULES.has(activeModule) || homeFullBleed || generatingFullBleed
  const panelClassName = fullBleed
    ? 'flex min-h-0 flex-1 flex-col'
    : 'flex min-h-0 flex-1 flex-col p-6'

  return (
    // Session-replay boundary (see src/lib/privacyMask.tsx): the module
    // workspace is replay-visible — games, books, canvas, placement, uploads.
    // Learner-name renders inside are individually re-masked (<MaskedName>,
    // enforced by scripts/check-privacy-mask.sh). Chat (RightPanel), the
    // learner sidebar (LeftPanel), and the header stay masked-by-default.
    <main className="flex min-w-0 flex-1 flex-col overflow-y-auto" {...PH_UNMASK}>
      <div className={panelClassName}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeModule}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="flex min-h-0 flex-1 flex-col"
          >
            {children ?? (
              <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
                <p className="text-lg font-medium">
                  {MODULE_LABELS[activeModule] ?? activeModule}
                </p>
                <p className="mt-1 text-sm">
                  Content for {MODULE_LABELS[activeModule]?.toLowerCase() ?? 'this module'} will appear here.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </main>
  )
}
